import { Link, useParams } from "react-router-dom";
import { PaymentCard } from "../application-card-details";
import { useGetLicenceApprovalStatus, useGetLicenseDetails, useGetPaymentByApplicationId, useResubmitLicence } from "../../../repositories/licences-repository";
import { TabContainer, TabPanelProp } from "@/components/Tabs";
import  { useState } from "react";
import { SquareArrowLeft } from "lucide-react";


import ApplicationDetailsCard from "../application-card-details";
import PaymentDetailsSlideOver from "../payments/payment-details-slide-over";
import InitiatePaymentModal from "../payments/initiate-payment-modal";
import Button from "@/components/buttons/Button";
import ApprovalProgressPanel from "../approval-progress-panel";
import ReturnedForCorrectionNotice from "../returned-for-correction-notice";
import LicenceDetailsCard, { ResidenceInfo } from "./licence-details-card";
import LicenceWorkforceDetails, { WorkforceCategory } from "./application-licence-details-work-force";
import TourCompanyList from "../tour-company-list";
import { LICENCE_INSPECTION_FEE } from "../../../constants/fees";
import useAuth from "@/providers/auth/useAuth";
import  {
    Attachments
} from "@/features/applications/pages/fragments/investments/application-investment-details-attachemnts.tsx";


/**
 * Everything on this screen is read from `LicenseRequestDTO` (GET /licence/{id}).
 * That DTO is flat: it has no `businessDetails` object, no applicant TIN and no
 * `describeTargetAudience`, so those rows are not rendered at all rather than
 * being filled with a placeholder. Fees come from the sub-category schedule the
 * DTO now returns, and room counts from `residences`.
 */
export default function ApplicationLicenceDetails() {
    const [show, setShow] = useState<boolean>(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    const { applicationId } = useParams()
    const { user } = useAuth()
    const { licence_details,isLoading,isFetching} = useGetLicenseDetails(applicationId!)
    const { payment, isLoading: isPaymentLoading, refetch: refetchPayment } = useGetPaymentByApplicationId(applicationId!)
    const {
        approval_status,
        isLoading: isApprovalLoading,
        isError: isApprovalError,
    } = useGetLicenceApprovalStatus(applicationId!)
    const resubmit = useResubmitLicence(applicationId!)
    const tabs: TabPanelProp[] = [
        { name: "Details", identifier: "details" },
        { name: "Operations", identifier: "operations" },
        { name: "Attachments", identifier: "attachments" },
    ]
    // Try both structures - sometimes API returns data directly, sometimes nested in results
    const licenceDetails = licence_details?.results || licence_details
    const approval = approval_status?.results ?? undefined

    // Helper function to safely convert to number or return 0
    const safeNumber = (value: any): number => {
        if (value === null || value === undefined || value === "") return 0;
        const num = Number(value);
        return isNaN(num) ? 0 : num;
    };

    /**
     * A workforce group is built only when the licence actually carries at least
     * one of its six counts. With no capture screen for employment details these
     * are absent for most applications, and a group of zeroes would read as a
     * recorded headcount of none rather than as "never asked".
     */
    const toWorkforceCategory = (
        managementMale: any, managementFemale: any,
        skilledMale: any, skilledFemale: any,
        unskilledMale: any, unskilledFemale: any,
    ): WorkforceCategory | undefined => {
        const values = [managementMale, managementFemale, skilledMale, skilledFemale, unskilledMale, unskilledFemale];
        if (values.every((value) => value === null || value === undefined || value === "")) return undefined;
        return {
            management: { male: safeNumber(managementMale), female: safeNumber(managementFemale) },
            skilled: { male: safeNumber(skilledMale), female: safeNumber(skilledFemale) },
            unskilled: { male: safeNumber(unskilledMale), female: safeNumber(unskilledFemale) },
            totalEmployees: values.reduce((total: number, value) => total + safeNumber(value), 0),
        };
    };

    const zanzibaris = toWorkforceCategory(
        licenceDetails?.zanzibarisManagmentMale, licenceDetails?.zanzibarisManagmentFemale,
        licenceDetails?.zanzibarisSkilledMale, licenceDetails?.zanzibarisSkilledFemale,
        licenceDetails?.zanzibarisUnskilledMale, licenceDetails?.zanzibarisUnskilledFemale,
    )
    const mainlanders = toWorkforceCategory(
        licenceDetails?.tanzaniaMainlandersManagmentMale, licenceDetails?.tanzaniaMainlandersManagmentFemale,
        licenceDetails?.tanzaniaMainlandersSkilledMale, licenceDetails?.tanzaniaMainlandersSkilledFemale,
        licenceDetails?.tanzaniaMainlandersUnskilledMale, licenceDetails?.tanzaniaMainlandersUnskilledFemale,
    )
    const foreigners = toWorkforceCategory(
        licenceDetails?.foreignersManagmentMale, licenceDetails?.foreignersManagmentFemale,
        licenceDetails?.foreignersSkilledMale, licenceDetails?.foreignersSkilledFemale,
        licenceDetails?.foreignersUnskilledMale, licenceDetails?.foreignersUnskilledFemale,
    )

    // Room classes: `LicenseRequestDTO.ResidenceInfo` carries roomClass, totalRooms
    // (a string) and price. The room count is their sum — there is no
    // `numberOfRooms` field anywhere on a licence.
    const residences: ResidenceInfo[] = licenceDetails?.residences ?? []
    const numberOfRooms = residences.length > 0
        ? residences.reduce((total, residence) => total + safeNumber(residence.totalRooms), 0)
        : undefined

    // Fee breakdown: the sub-categories now come back with their fee schedule.
    // The inspection fee is a client-side constant (see constants/fees.ts), and
    // `feeAmount` is the amount actually billed on GePG once a bill exists.
    const subcategories: Array<{ name?: string; baseFee?: number; managementFee?: number }> =
        licenceDetails?.subcategories ?? []
    const sumFee = (key: "baseFee" | "managementFee") =>
        subcategories.reduce((total, subcategory) => total + safeNumber(subcategory[key]), 0)
    const baseFee = subcategories.length > 0 ? sumFee("baseFee") : undefined
    const managementFee = subcategories.length > 0 ? sumFee("managementFee") : undefined
    const inspectionFee = subcategories.length > 0 ? LICENCE_INSPECTION_FEE : undefined
    const billedAmount = licenceDetails?.feeAmount != null ? safeNumber(licenceDetails.feeAmount) : undefined
    const calculatedTotal = baseFee !== undefined
        ? baseFee + (managementFee ?? 0) + (inspectionFee ?? 0)
        : undefined
    const totalFees = payment?.amount != null
        ? safeNumber(payment.amount)
        : billedAmount ?? calculatedTotal

    const tourCompanies: any[] = licenceDetails?.tourCompanies ?? []

    // The licence itself records no applicant name or TIN — both live on the
    // account's business record, which is the same applicant on this portal.
    const customerName = licenceDetails?.businessName ?? user?.business?.businessName
    const customerTin = user?.business?.businessTinNumber

    const sampleAttachments = [
        {
            id: '1',
            name: 'Tax Clearance Document',
            url: licenceDetails?.taxClearancefromZRAandTRADocumentString,
            // type: 'application/pdf'
        },
        {
            id: '2',
            name: 'Hotel Tariffs Document',
            url: licenceDetails?.hotelTariffsDocumentString,
            // url: 'path/to/hotel_tariffs.pdf',
            // type: 'application/pdf'
        },
        {
            id: '3',
            name: 'Sea Worthiness Certificate',
            url: licenceDetails?.seaWorthinessCertificateDocumentString,
            // type: 'application/pdf'
        },
        {
            id: '4',
            name: 'Certificate of Incorporation',
            url: licenceDetails?.certificationIncorporation,
            // type: 'application/pdf'
        }
    ].filter(attachment => attachment.url && attachment.url.trim() !== '');

    // Show loading state while data is being fetched
    if (isLoading || isFetching || isPaymentLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                </div>
            </div>
        )
    }

    return (
        <div className="pb-5">
            <header className="bg-white px-4 py-2 border-b border-t border-secondary-100 flex justify-between items-center gap-4">
                <Link to="/applications/licences">
                    <Button
                        intent="text"
                        leftIcon={<SquareArrowLeft className="size-5" />}
                        className="text-base text-secondary font-light"
                    >
                        Application Details
                    </Button>
                </Link>
            </header>
            <div className="mt-3 px-4">

                <ReturnedForCorrectionNotice
                    approval={approval}
                    fallbackReason={licenceDetails?.statusComment}
                    onResubmit={() => resubmit.mutate()}
                    isResubmitting={resubmit.isPending}
                    error={resubmit.error}
                />

                <InitiatePaymentModal
                    isOpen={showPaymentModal}
                    onClose={() => setShowPaymentModal(false)}
                    amount={totalFees ?? 0}
                    // Links the bill to this licence so attachBill can surface the control
                    // number, fee and payment status on the application.
                    applicationId={licenceDetails?.applicationId}
                    billDesc={`Tourism Licence Fee — ${licenceDetails?.projectName ?? ''}`}
                    custName={customerName}
                    custEmail={licenceDetails?.principalBusinessLocationEmail}
                    custAccnt={licenceDetails?.applicationId}
                    custTin={customerTin}
                    custIdTyp={customerTin ? '4' : undefined}
                    custId={customerTin ?? licenceDetails?.applicationId}
                    defaultPhoneNumber={licenceDetails?.principalBusinessLocationPhoneNumber}
                />

                <PaymentDetailsSlideOver
                    setShow={() => setShow(!show)}
                    show={show}
                    controlNumber={payment?.controlNumber ?? licenceDetails?.controlNumber}
                    onReused={() => refetchPayment()}
                    paymentData={payment ? {
                        customerName: customerName ?? payment?.customerName,
                        projectName: licenceDetails?.projectName || payment?.projectName,
                        referenceNumber: payment?.referenceNumber,
                        paymentStatus: payment?.status || payment?.paymentStatus,
                        amountPaid: payment?.amount,
                        paymentMethod: payment?.paymentType || payment?.paymentMethod,
                        phoneNumber: licenceDetails?.principalBusinessLocationPhoneNumber || payment?.phoneNumber,
                        referenceId: payment?.referenceNumber || payment?.referenceId,
                        date: payment?.paymentDate || payment?.createdAt,
                        generatedDate: payment?.billGenDt,
                        expiryDate: payment?.billExprDt,
                        billedItem: payment?.billedItem,
                        amountInWords: payment?.amountInWords,
                        preparedBy: payment?.preparedBy
                    } : undefined}
                />

                <div className="sm:flex sm:justify-between w-full gap-5">
                    <ApplicationDetailsCard
                            status={licenceDetails?.applicationStatus ?? "Unknown"}
                            paymentStatus={payment?.status ?? payment?.paymentStatus ?? licenceDetails?.paymentStatus}
                            title={licenceDetails?.businessName ?? licenceDetails?.projectName ?? "Licence application"}
                            applicationId={licenceDetails?.applicationId ?? "—"}
                            submissionDate={licenceDetails?.dateIssues}
                            applicationStatus={licenceDetails?.applicationStatus ?? "Unknown"}
                            projectName={licenceDetails?.projectName ?? "—"}
                            projectPOBox={licenceDetails?.principalBusinessLocationPoBox}
                            emailAddress={licenceDetails?.principalBusinessLocationEmail}
                            phoneNumber={licenceDetails?.principalBusinessLocationPhoneNumber}
                            projectLocation={licenceDetails?.street ?? licenceDetails?.physicalLocation}
                            numberOfRooms={numberOfRooms}
                        />
                    <div className="flex flex-col w-full gap-5">
                        <TabContainer panels={tabs} panelClassName="mt-3">
                            <div>
                                <LicenceDetailsCard
                                    titleHeader="Details"
                                    licenseCategory={licenceDetails?.licenseCategory?.name}
                                    accommodationCategory={subcategories[0]?.name ?? licenceDetails?.selectedServices?.[0]?.label}
                                    accommodationType={licenceDetails?.accommodationType}
                                    totalRooms={numberOfRooms}
                                    estimatedFee={totalFees}
                                    feeCurrency={licenceDetails?.feeCurrency}
                                    licenceNumber={licenceDetails?.licenseNumber}
                                    issuedDate={licenceDetails?.issuedDate}
                                    expiryDate={licenceDetails?.expiryDate}
                                    residences={residences}
                                    shareholders={licenceDetails?.businessShareHolders ?? []}
                                />

                                <PaymentCard
                                    reference={payment?.referenceNumber ?? licenceDetails?.controlNumber}
                                    baseFee={baseFee?.toString()}
                                    managementFee={managementFee?.toString()}
                                    inspectionFee={inspectionFee?.toString()}
                                    totalFees={totalFees?.toString()}
                                    currency={licenceDetails?.feeCurrency}
                                    paymentDate={payment?.paymentDate ?? payment?.createdAt}
                                    onGenerateControlNumber={() => setShowPaymentModal(true)}
                                    viewDetailsHandler={() => setShow(true)}
                                />

                                <ApprovalProgressPanel
                                    approval={approval}
                                    isLoading={isApprovalLoading}
                                    isError={isApprovalError}
                                    title="Licence Approval Progress"
                                />

                            </div>

                            <div className="w-full">
                                <LicenceWorkforceDetails
                                    zanzibaris={zanzibaris}
                                    mainlanders={mainlanders}
                                    foreigners={foreigners}
                                />
                                <div className="w-full">
                                    <h1 className="text-lg font-semibold text-[#2C2C2C] mt-2">Tour Companies Servicing This Project</h1>
                                        <div className="space-y-2">
                                            {tourCompanies.length > 0 ? (
                                                <TourCompanyList
                                                    showActionButtons={false}
                                                    tourCompanies={tourCompanies}
                                                    onEdit={() => {}}
                                                    onRemove={() => { }}
                                                />
                                            ) : (
                                                <p className="text-sm text-gray-500 mt-2">
                                                    No tour companies recorded for this application.
                                                </p>
                                            )}
                                        </div>

                                </div>
                            </div>

                            <div>
                                <Attachments attachments={sampleAttachments}/>

                            </div>


                        </TabContainer>
                    </div>
                </div>
            </div>
        </div>
    )
}
